
import React, { useState, useEffect } from 'react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { User, Mail, Phone, Car, CalendarRange, CircleDollarSign, Receipt, Info, AlertTriangle, FileText, X } from "lucide-react";
import { format, addDays, differenceInDays } from "date-fns";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { useSiteBookings } from "@/hooks/useSiteBookings";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";
import { useCart, CartItem } from "@/contexts/CartContext";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";

const formSchema = z.object({
  fullName: z.string().min(2, {
    message: "Full name must be at least 2 characters."
  }),
  email: z.string().email({
    message: "Please enter a valid email address."
  }),
  phone: z.string().min(10, {
    message: "Please enter a valid phone number."
  }),
  vehicleLicensePlate: z.string().min(1, {
    message: "Vehicle license plate is required."
  }),
  trailerLicensePlate: z.string().optional(),
  state: z.string().min(1, {
    message: "State is required."
  }),
  city: z.string().optional(),
  zipCode: z.string().optional(),
  comments: z.string().optional(),
  permitType: z.enum(["camping", "dayUse"]),
  stayType: z.enum(["nightly", "weekly", "monthly"]),
  termsAgreed: z.boolean().refine(val => val === true, {
    message: "You must accept the camping regulations."
  }),
  rvDumpFee: z.boolean().default(false)
});

type BookingFormValues = z.infer<typeof formSchema>;

interface WildHorseBookingFormProps {
  siteId: string;
  siteName: string;
  price: number;
  isPaved: boolean;
  onSuccess: () => void;
  editData?: CartItem | null; // Add editData prop
}

const WildHorseBookingForm: React.FC<WildHorseBookingFormProps> = ({
  siteId,
  siteName,
  price,
  isPaved,
  onSuccess,
  editData
}) => {
  const { toast } = useToast();
  const { addItem } = useCart();
  const { bookings, isLoading: bookingsLoading } = useSiteBookings(siteId);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [calculatedDays, setCalculatedDays] = useState(0);
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [safetyDialogOpen, setSafetyDialogOpen] = useState(false);
  const [legalDialogOpen, setLegalDialogOpen] = useState(false);
  const navigate = useNavigate();

  const pavedNightlyRate = 15;
  const pavedWeeklyRate = 75;
  const pavedMonthlyRate = 150;
  const unpavedNightlyRate = 10;
  const unpavedWeeklyRate = 50;
  const unpavedMonthlyRate = 125;
  const dayUseRate = 5;
  const rvDumpFee = 10;

  const form = useForm<BookingFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      vehicleLicensePlate: "",
      trailerLicensePlate: "",
      state: "",
      city: "",
      zipCode: "",
      comments: "",
      permitType: "camping",
      stayType: "nightly",
      termsAgreed: false,
      rvDumpFee: false
    }
  });

  const watchPermitType = form.watch("permitType");
  const watchStayType = form.watch("stayType");
  const watchRvDumpFee = form.watch("rvDumpFee");

  useEffect(() => {
    if (startDate && endDate) {
      const days = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      console.log("Date range selected: ", { startDate, endDate, days });
      
      if (days >= 10) {
        console.log("Switching to monthly rate");
        form.setValue("stayType", "monthly");
      } else if (days >= 5) {
        console.log("Switching to weekly rate");
        form.setValue("stayType", "weekly");
      } else {
        console.log("Switching to nightly rate");
        form.setValue("stayType", "nightly");
      }
    }
  }, [startDate, endDate, form]);

  // When the user picks a stay-length radio (nightly/weekly/monthly) and an
  // arrival date already exists, auto-fill the departure date so the picker
  // and pricing reflect the chosen length immediately.
  useEffect(() => {
    if (!startDate) return;
    const targetNights =
      watchStayType === "monthly" ? 30 : watchStayType === "weekly" ? 7 : 1;
    const currentNights = endDate
      ? Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
      : 0;
    if (currentNights === targetNights) return;
    // Only auto-fill if current range is empty OR doesn't already justify the
    // selected radio (avoids fighting the date→radio effect above).
    const matchesCurrent =
      (watchStayType === "monthly" && currentNights >= 9) ||
      (watchStayType === "weekly" && currentNights >= 4 && currentNights < 9) ||
      (watchStayType === "nightly" && currentNights < 4 && currentNights > 0);
    if (matchesCurrent) return;
    const departure = addDays(startDate, targetNights);
    setEndDate(departure);
  }, [watchStayType, startDate]); // intentionally exclude endDate to avoid loops


  useEffect(() => {
    if (watchPermitType === "dayUse") {
      setCalculatedPrice(dayUseRate + (watchRvDumpFee ? rvDumpFee : 0));
      return;
    }

    if (startDate && endDate) {
      const days = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      setCalculatedDays(days);
      let basePrice = 0;
      if (watchStayType === "nightly") {
        basePrice = isPaved ? pavedNightlyRate * days : unpavedNightlyRate * days;
      } else if (watchStayType === "weekly") {
        const weeks = Math.ceil(days / 7);
        basePrice = isPaved ? pavedWeeklyRate * weeks : unpavedWeeklyRate * weeks;
      } else if (watchStayType === "monthly") {
        const months = Math.ceil(days / 30);
        basePrice = isPaved ? pavedMonthlyRate * months : unpavedMonthlyRate * months;
      }
      const totalPrice = watchRvDumpFee ? basePrice + rvDumpFee : basePrice;
      setCalculatedPrice(totalPrice);
    } else {
      setCalculatedDays(0);
      setCalculatedPrice(0);
    }
  }, [startDate, endDate, watchStayType, watchRvDumpFee, isPaved, watchPermitType]);

  const handleDateRangeChange = (start: Date | undefined, end: Date | undefined) => {
    console.log("Date range changed:", { start, end });
    setStartDate(start);
    setEndDate(end);
  };

  const handleSubmit = (values: BookingFormValues) => {
    if (values.permitType !== "dayUse" && (!startDate || !endDate)) {
      toast({
        title: "Missing Dates",
        description: "Please select your arrival and departure dates",
        variant: "destructive"
      });
      return;
    }

    if (calculatedPrice <= 0) {
      toast({
        title: "Invalid Stay",
        description: "This stay comes to $0. Please check your dates and options.",
        variant: "destructive"
      });
      return;
    }


    const uniqueCartItemId = `wildhorse-${siteId}-${Date.now()}`;
    
    addItem({
      id: uniqueCartItemId,
      siteId: siteId,
      type: values.permitType === "dayUse" ? "day-use" as const : "camping" as const,
      permitCategory: values.permitType === "dayUse" ? "day-use" : "camping",
      permitName: `${siteName} - ${values.permitType === "dayUse" ? "Day Use" : values.stayType}`,
      isAnnual: false,
      customerName: values.fullName,
      customerEmail: values.email,
      startDate: values.permitType === "dayUse" ? new Date() : (startDate as Date),
      endDate: values.permitType === "dayUse" ? new Date() : (endDate as Date),
      includesCamping: values.permitType !== "dayUse",
      basePrice: values.permitType === "dayUse" ? dayUseRate : (isPaved ? pavedNightlyRate : unpavedNightlyRate),
      totalPrice: calculatedPrice,
      phoneNumber: values.phone,
      city: values.city || "",
      state: values.state,
      zipCode: values.zipCode || "",
      licensePlate: values.vehicleLicensePlate,
      additionalDetails: (values.trailerLicensePlate ? `Trailer: ${values.trailerLicensePlate}. ` : '') + (values.comments || ''),
      stayType: values.stayType,
      rvDumpFee: values.rvDumpFee
    });

    toast({
      title: "Added to cart!",
      description: `${values.permitType === "dayUse" ? "Day use" : "Camping"} permit for ${siteName} added to cart.`
    });

    navigate("/cart");
  };

  return <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        
        <div className="bg-tribal-primary/5 rounded-md p-4 mb-2">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/lovable-uploads/3a7cfada-f83b-4999-ab0e-39413f4147f0.jpg" 
              alt="Wildhorse Camping Form" 
              className="w-20 h-20 opacity-30" 
            />
            <div className="ml-3">
              <h3 className="text-lg font-semibold">Wildhorse Reservoir</h3>
              <p className="text-sm text-tribal-neutral">Shoshone-Paiute Tribes - Fish, Wildlife and Parks Dept. (208) 759-3246</p>
            </div>
          </div>
          
          <div className="text-sm border border-dashed border-tribal-neutral/50 p-3 rounded bg-white">
            <h4 className="font-medium mb-2 text-center">CAMPING RATES</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className={isPaved ? "" : "opacity-50"}>
                <h5 className="font-medium">PAVED (Main Campgrounds)</h5>
                <ul className="space-y-1">
                  <li>Nightly: $15.00 /vehicle</li>
                  <li>Weekly: $75.00</li>
                  <li>Monthly: $150.00</li>
                </ul>
              </div>
              <div className={!isPaved ? "" : "opacity-50"}>
                <h5 className="font-medium">UNPAVED</h5>
                <ul className="space-y-1">
                  <li>Nightly: $10.00 /vehicle</li>
                  <li>Weekly: $50.00</li>
                  <li>Monthly: $125.00</li>
                </ul>
              </div>
            </div>
            <div className="mt-2 text-center">
              <p>Day use fees are charged per vehicle: $5.00 / PER VEHICLE</p>
              <p>RV Dump Fee: $10.00</p>
            </div>
          </div>
          
          <div className="mt-3 text-sm">
            <ul className="space-y-1 text-tribal-neutral">
              <li>• Fees are charged per vehicle, per day.</li>
              <li>• Please display your receipt on driver's side of vehicle.</li>
              <li>• Make checks payable to "Shoshone-Paiute Tribes"</li>
            </ul>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium mb-4 flex items-center">
            <Receipt className="h-4 w-4 mr-2 text-tribal-primary" />
            Select Permit Type
          </h3>

          <FormField
            control={form.control}
            name="permitType"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex flex-col space-y-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="camping" id="camping" />
                      <FormLabel htmlFor="camping" className="font-normal">
                        Camping Permit
                      </FormLabel>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="dayUse" id="dayUse" />
                      <FormLabel htmlFor="dayUse" className="font-normal">
                        Day Use Only ($5.00 per vehicle)
                      </FormLabel>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        {form.watch("permitType") === "camping" && (
          <>
            <div>
              <h3 className="text-sm font-medium mb-2 flex items-center">
                <CalendarRange className="h-4 w-4 mr-2 text-tribal-primary" />
                Select Dates
              </h3>
              <DateRangePicker 
                startDate={startDate} 
                endDate={endDate} 
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                startLabel="Arrival Date" 
                endLabel="Departure Date" 
                className="w-full"
                bookings={bookings}
                bookingsLoading={bookingsLoading}
                numberOfMonths={2}
                stayPresets={[
                  { label: "1 Night", nights: 1 },
                  { label: "1 Week", nights: 7 },
                  { label: "1 Month", nights: 30 },
                ]}
                onInvalidRange={(message) => {
                  toast({
                    title: "Dates unavailable",
                    description: message,
                    variant: "destructive",
                  });
                }}
              />
            </div>
            
            <div>
              <h3 className="text-sm font-medium mb-2 flex items-center">
                <Receipt className="h-4 w-4 mr-2 text-tribal-primary" />
                Reservation Type
              </h3>
              <FormField control={form.control} name="stayType" render={({
                field
              }) => <FormItem className="space-y-3">
                      <FormControl>
                        <RadioGroup onValueChange={field.onChange} value={field.value} className="flex flex-col space-y-1">
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="nightly" id="nightly" />
                            <FormLabel htmlFor="nightly" className="font-normal">
                              Nightly ({isPaved ? "$15" : "$10"} per night)
                            </FormLabel>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="weekly" id="weekly" />
                            <FormLabel htmlFor="weekly" className="font-normal">
                              Weekly ({isPaved ? "$75" : "$50"} per week)
                            </FormLabel>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="monthly" id="monthly" />
                            <FormLabel htmlFor="monthly" className="font-normal">
                              Monthly ({isPaved ? "$150" : "$125"} per month)
                            </FormLabel>
                          </div>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />
            </div>
          </>
        )}

        <div className="mt-4">
          <FormField control={form.control} name="rvDumpFee" render={({
            field
          }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      Add RV Dump Fee ($10.00)
                    </FormLabel>
                  </div>
                </FormItem>} />
        </div>
        
        <Separator />
        
        <div>
          <h3 className="text-sm font-medium mb-4 flex items-center">
            <User className="h-4 w-4 mr-2 text-tribal-primary" />
            Contact Information
          </h3>
          
          <div className="grid grid-cols-1 gap-4">
            <FormField control={form.control} name="fullName" render={({
            field
          }) => <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            
            <FormField control={form.control} name="email" render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="your@email.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            
            <FormField control={form.control} name="phone" render={({
            field
          }) => <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Phone Number
                  </FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="(555) 123-4567" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>
        
        <div>
          <h3 className="text-sm font-medium mb-4 flex items-center">
            <Car className="h-4 w-4 mr-2 text-tribal-primary" />
            Vehicle Information
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField control={form.control} name="vehicleLicensePlate" render={({
            field
          }) => <FormItem>
                  <FormLabel>Vehicle License Plate #</FormLabel>
                  <FormControl>
                    <Input placeholder="ABC123" {...field} className="uppercase" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            
            <FormField control={form.control} name="trailerLicensePlate" render={({
            field
          }) => <FormItem>
                  <FormLabel>Trailer Plate # (if applicable)</FormLabel>
                  <FormControl>
                    <Input placeholder="XYZ789" {...field} className="uppercase" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            
            <FormField control={form.control} name="state" render={({
            field
          }) => <FormItem>
                  <FormLabel>State</FormLabel>
                  <FormControl>
                    <Input placeholder="NV" {...field} maxLength={2} className="uppercase" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />
            
            {form.watch("permitType") === "camping" && isPaved && <div className="flex flex-col">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Campsite # (paved only)
                </label>
                <Input value={siteName} disabled className="bg-gray-100 mt-2" />
              </div>}
          </div>
        </div>
        
        <FormField control={form.control} name="comments" render={({
        field
      }) => <FormItem>
              <FormLabel>Comments</FormLabel>
              <FormControl>
                <Input placeholder="Additional comments or special requests" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        
        <div className="bg-tribal-primary/5 p-4 rounded-md">
          <h3 className="text-sm font-medium mb-2 flex items-center">
            <CircleDollarSign className="h-4 w-4 mr-2 text-tribal-primary" />
            Pricing Summary
          </h3>
          
          <div className="space-y-1 text-sm">
            {form.watch("permitType") === "dayUse" ? (
              <div className="flex justify-between">
                <span>Day use fee:</span>
                <span>$5.00</span>
              </div>
            ) : (
              <>
                <div className="flex justify-between">
                  <span>Campsite type:</span>
                  <span>{isPaved ? "Paved" : "Unpaved"}</span>
                </div>
                <div className="flex justify-between">
                  <span>Stay type:</span>
                  <span className="capitalize">{watchStayType}</span>
                </div>
                {watchStayType === "nightly" && <div className="flex justify-between">
                    <span>Number of nights:</span>
                    <span>{calculatedDays}</span>
                  </div>}
                {watchStayType === "weekly" && <div className="flex justify-between">
                    <span>Number of weeks:</span>
                    <span>{Math.ceil(calculatedDays / 7)}</span>
                  </div>}
                {watchStayType === "monthly" && <div className="flex justify-between">
                    <span>Number of months:</span>
                    <span>{Math.ceil(calculatedDays / 30)}</span>
                  </div>}
              </>
            )}
            {watchRvDumpFee && <div className="flex justify-between">
                <span>RV Dump Fee:</span>
                <span>$10.00</span>
              </div>}
            <Separator className="my-2" />
            <div className="flex justify-between font-medium">
              <span>Total amount:</span>
              <span>{formatCurrency(calculatedPrice)}</span>
            </div>
          </div>
        </div>
        
        <Dialog open={safetyDialogOpen} onOpenChange={setSafetyDialogOpen}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Safety Requirements
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm">
              <p className="text-tribal-neutral mb-4">Important safety information for camping on tribal lands</p>
              
              <h3 className="font-semibold mb-2">Personal Safety Requirements:</h3>
              <ul className="list-disc pl-5 space-y-2 mb-4">
                <li>No swimming or wading in reservoir spillways or dam areas</li>
                <li>No alcohol consumption while operating watercraft</li>
                <li>Maintain safe distances from wildlife</li>
                <li>Be aware of changing weather conditions</li>
                <li>Carry sufficient water and sun protection</li>
                <li>Observe posted speed limits</li>
              </ul>
              
              <h3 className="font-semibold mb-2">Environmental Safety:</h3>
              <ul className="list-disc pl-5 space-y-2 mb-4">
                <li>Properly dispose of trash and waste</li>
                <li>Clean all equipment to prevent invasive species</li>
                <li>Observe fire restrictions during dry seasons</li>
                <li>Respect wildlife and maintain safe distances</li>
                <li>Keep vehicles on designated roads and camping areas</li>
                <li>Report any hazardous conditions to tribal authorities</li>
              </ul>
              
              <h3 className="font-semibold mb-2">RV-Specific Safety:</h3>
              <ul className="list-disc pl-5 space-y-2 mb-4">
                <li>Use proper electrical connections and avoid overloading circuits</li>
                <li>Secure all propane tanks and check for leaks regularly</li>
                <li>Keep a fire extinguisher accessible</li>
                <li>Use stabilizers when parked</li>
                <li>Respect quiet hours from 10pm to 6am</li>
              </ul>
              
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <h3 className="font-semibold mb-1">Emergency contact:</h3>
                <p>For emergencies, call Tribal Police at (775) 757-3624 (Owyhee Police Department). Cell service is limited in many areas, so plan accordingly.</p>
              </div>
            </div>
            <div className="flex justify-center mt-2">
              <Button onClick={() => setSafetyDialogOpen(false)} className="bg-emerald-500 hover:bg-emerald-600">
                I Understand
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        <Dialog open={legalDialogOpen} onOpenChange={setLegalDialogOpen}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-tribal-primary" />
                Legal Terms
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm">
              <p className="text-tribal-neutral mb-4">Legal terms and conditions for camping on Shoshone-Paiute Tribal lands</p>
              
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-md mb-4">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  Legal and Safety Acknowledgment
                </h3>
                <p className="text-sm">
                  "BY EXECUTION OF A TRIBAL HUNTING, FISHING, TRAPPING, AND CAMPING PERMIT THE APPLICANT <span className="text-tribal-primary font-semibold">ACKNOWLEDGES</span> AND VOLUNTARILY CONSENTS TO CIVIL OR CRIMINAL ADJUDICATION IN THE SHOSHONE-PAIUTE TRIBAL COURT, FOR ANY INFRACTIONS OF THE CURRENT DUCK VALLEY HUNTING AND FISHING ORDINANCE, AND EXISTING FEDERAL STATUTES."
                </p>
              </div>
              
              <h3 className="font-semibold mb-2">Duck Valley Tribal Camping Ordinance</h3>
              <p className="mb-3">By purchasing a camping permit, you agree to be bound by all applicable tribal laws and regulations, including:</p>
              <ul className="list-disc pl-5 space-y-2 mb-4">
                <li>Adherence to camping regulations and quiet hours as posted at each camping site</li>
                <li>Compliance with all vehicle and RV restrictions in designated areas</li>
                <li>Proper waste disposal and environmental protection measures</li>
                <li>Recognition of tribal sovereignty and jurisdiction</li>
                <li>Submission to the authority of tribal conservation officers</li>
                <li>All permit holders must have their camping permit available for inspection at all times</li>
              </ul>
              
              <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                <p className="text-sm">
                  Violation of these terms may result in citation, fine, confiscation of equipment, or exclusion from tribal lands. All legal matters arising from your activities on tribal lands will be adjudicated by the Shoshone-Paiute Tribal Court.
                </p>
              </div>
            </div>
            <div className="flex justify-center mt-2">
              <Button onClick={() => setLegalDialogOpen(false)} className="bg-emerald-500 hover:bg-emerald-600">
                I Understand
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        <div>
          <FormField control={form.control} name="termsAgreed" render={({
          field
        }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-sm">
                    I acknowledge and agree to the <Button 
                      variant="link" 
                      className="p-0 h-auto text-tribal-primary underline hover:text-tribal-primary/80" 
                      type="button"
                      onClick={() => setLegalDialogOpen(true)}
                    >
                      legal terms
                    </Button> and <Button 
                      variant="link" 
                      className="p-0 h-auto text-tribal-primary underline hover:text-tribal-primary/80" 
                      type="button"
                      onClick={() => setSafetyDialogOpen(true)}
                    >
                      safety
                    </Button> requirements for camping on tribal lands
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>} />
        </div>
        
        
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800">
              The Shoshone-Paiute Tribes offer general camping at Wild Horse Reservoir. However, please note that the Tribes do not own or manage fishing rights at Wild Horse Reservoir. Therefore, anyone planning to fish will need to obtain a valid Nevada State Fishing License separately.
            </p>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Button type="submit" className="flex-1 bg-tribal-primary hover:bg-tribal-primary/90">
            Add to Cart
          </Button>
          <Button type="button" variant="outline" className="flex-1" onClick={onSuccess}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>;
};

export default WildHorseBookingForm;
